import { useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { buildFacetGraph } from "../services/buildFacetGraph";
import { fetchFacets } from "../services/fetchFacets";
import type { CountBucket, UseOverviewGraphParams } from "../types";

const toBuckets = (facets: { key: string; doc_count: number }[]): CountBucket[] =>
    facets.map((facet) => ({ value: facet.key, count: facet.doc_count }));

export const useOverviewGraph = ({
    index,
    rootLabel,
    category,
    subCategory,
    expanded,
}: UseOverviewGraphParams) => {
    const { instance } = useMsal();

    return useQuery({
        queryKey: ["overview-facets", index, category, subCategory, expanded],
        enabled: Boolean(index) && Boolean(category),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        placeholderData: (previous) => previous,
        queryFn: async () => {
            const base = await fetchFacets({ instance, index, filters: [] });
            const categories = toBuckets(
                base.find((entry) => entry.field === category)?.facets ?? [],
            );

            const shouldExpand = Boolean(expanded) && Boolean(subCategory);
            const scoped = shouldExpand
                ? await fetchFacets({
                      instance,
                      index,
                      filters: [{ key: category, value: [expanded ?? ""] }],
                  })
                : [];
            const subItems = shouldExpand
                ? toBuckets(scoped.find((entry) => entry.field === subCategory)?.facets ?? [])
                : [];

            return buildFacetGraph({
                categories,
                categoryField: category,
                rootLabel,
                expandedValue: expanded,
                subItems,
                subField: subCategory,
            });
        },
    });
};
